import { useState } from 'preact/hooks';
import type { Theme } from '../styles/theme.js';
import { renderMarkdown } from '../lib/markdown.js';
import type { Attachment, Message } from '../state/store.js';
import { STRINGS } from '../strings.js';

type Props = {
  message: Message;
  theme: Theme;
  // Quick win §9 : rappelé avec l'id local du message en échec quand le
  // visiteur clique "Réessayer" (cf. App.tsx deliver()).
  onRetry?: ((localId: string) => void) | undefined;
};

/**
 * D16 : les URL signées des pièces jointes expirent au bout de 7 jours
 * (cf. `attachments/urls.ts` côté serveur). Un message restauré depuis
 * localStorage après ce délai pointe vers une URL qui répond 403 — sans ce
 * composant, `<img>` afficherait l'icône "image cassée" du navigateur.
 * `onError` bascule sur un placeholder textuel à la place.
 */
function AttachmentImage({ attachment }: { attachment: Attachment }) {
  const [expired, setExpired] = useState(false);

  if (expired) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '160px',
          height: '120px',
          borderRadius: '10px',
          backgroundColor: 'rgba(0, 0, 0, 0.06)',
          color: '#666',
          fontSize: '12px',
          fontStyle: 'italic',
        }}
      >
        {STRINGS.attachImageExpired}
      </div>
    );
  }

  return (
    <a
      href={attachment.url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={STRINGS.attachOpen}
      style={{ display: 'block' }}
    >
      <img
        src={attachment.url}
        alt={STRINGS.attachAlt}
        loading="lazy"
        onError={() => setExpired(true)}
        style={{
          display: 'block',
          maxWidth: '100%',
          maxHeight: '240px',
          borderRadius: '10px',
          cursor: 'pointer',
        }}
      />
    </a>
  );
}

/**
 * Pièce jointe non-image (PDF, feat/attachments-files) reçue de l'agent :
 * l'input visiteur reste images seulement (`InputComposer.tsx` inchangé),
 * donc ce cas ne peut se produire que pour un message agent/IA. Rendue
 * comme un simple lien fichier — pas de visionneuse PDF maison à maintenir.
 */
function AttachmentFile({ attachment, theme }: { attachment: Attachment; theme: Theme }) {
  return (
    <a
      href={attachment.url}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 10px',
        borderRadius: '10px',
        backgroundColor: 'rgba(0, 0, 0, 0.06)',
        color: theme.brandColor,
        textDecoration: 'none',
        fontSize: '13px',
        maxWidth: '220px',
      }}
    >
      <span aria-hidden="true">📄</span>
      <span
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {attachment.filename ?? STRINGS.attachFileOpen}
      </span>
    </a>
  );
}

export function MessageBubble({ message, theme, onRetry }: Props) {
  const isVisitor = message.role === 'visitor';
  const isSystem = message.role === 'system';
  const attachments = message.attachments ?? [];

  if (isSystem) {
    return (
      <div
        style={{
          textAlign: 'center',
          fontSize: '12px',
          color: '#666',
          padding: '8px 16px',
          fontStyle: 'italic',
        }}
      >
        {message.content}
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isVisitor ? 'flex-end' : 'flex-start',
        marginBottom: '8px',
      }}
    >
      <div
        style={{
          maxWidth: '75%',
          padding: attachments.length > 0 && message.content.length === 0 ? '4px' : '8px 12px',
          borderRadius: '14px',
          backgroundColor: isVisitor ? theme.bubbleVisitorBg : theme.bubbleAiBg,
          color: isVisitor ? theme.bubbleVisitorText : theme.bubbleAiText,
          wordWrap: 'break-word',
          fontSize: '14px',
          lineHeight: '1.4',
        }}
      >
        {attachments.map((a) => (
          // Ouverture en plein écran = nouvel onglet sur l'URL signée : pas de
          // visionneuse maison à maintenir, et le pinch-to-zoom natif du
          // navigateur fait le travail sur mobile.
          <div
            key={a.id}
            style={{ marginBottom: message.content.length > 0 ? '6px' : '0' }}
          >
            {a.mime.startsWith('image/') ? (
              <AttachmentImage attachment={a} />
            ) : (
              <AttachmentFile attachment={a} theme={theme} />
            )}
          </div>
        ))}
        {message.content.length > 0 && (
          <div dangerouslySetInnerHTML={{ __html: renderMarkdown(message.content) }} />
        )}
      </div>
      {message.failed && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: '6px',
            fontSize: '11px',
            color: '#c0392b',
            marginTop: '2px',
          }}
        >
          <span>{STRINGS.sendFailed}</span>
          {onRetry && (
            <button
              type="button"
              onClick={() => onRetry(message.id)}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                color: theme.brandColor,
                textDecoration: 'underline',
                fontSize: '11px',
                cursor: 'pointer',
              }}
            >
              {STRINGS.sendRetry}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
